import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { OnboardingEscalationFields } from '../components/OnboardingEscalationFields';
export type GuardrailOption = {
  id: string;
  title: string;
  description: string;
  locked?: boolean;
};

export const GUARDRAIL_OPTIONS: readonly GuardrailOption[] = [
  {
    id: 'commerce-scope-only',
    title: 'Commerce scope only',
    description: 'Your agent only answers questions about your store — no off-topic responses.',
    locked: true,
  },
  {
    id: 'no-invented-answers',
    title: 'No invented answers',
    description:
      "If your agent doesn't know something, it says so and escalates rather than guessing.",
    locked: true,
  },
  {
    id: 'no-competitor-recommendations',
    title: 'No competitor recommendations',
    description: "Your agent won't suggest or validate competitor products or websites.",
    locked: true,
  },
  {
    id: 'escalate-on-complaint',
    title: 'Escalate on complaint or frustration',
    description: 'Your agent hands off to a human when a customer expresses anger or distress.',
    locked: true,
  },
  {
    id: 'no-unauthorized-discounts',
    title: 'No unauthorized discounts',
    description: "Your agent won't offer, extend, or imply discounts you haven't approved.",
    locked: true,
  },
  {
    id: 'escalate-high-value-orders',
    title: 'Escalate high-value orders',
    description:
      'Orders above your threshold are flagged for human review before completing checkout.',
    locked: true,
  },
];

const ESCALATE_HIGH_VALUE_ORDERS_GUARDRAIL_ID = 'escalate-high-value-orders';

const TOGGLEABLE_GUARDRAIL_IDS = GUARDRAIL_OPTIONS.filter((option) => !option.locked).map(
  (option) => option.id,
);

export const DEFAULT_GUARDRAIL_TOGGLES = Object.fromEntries(
  TOGGLEABLE_GUARDRAIL_IDS.map((id) => [id, true]),
) as Record<string, boolean>;

export const GuardrailsStep = (): JSX.Element => {
  return (
    <>
      <Box
        data-testid="ella-onboarding-guardrails-info-banner"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          px: 2,
          py: 1.5,
          mb: 3,
          borderRadius: '4px',
          bgcolor: 'secondary.light',
          border: '1px solid',
          borderColor: 'border.main',
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} aria-hidden />
        <Typography variant="small" color="text.secondary" sx={{ lineHeight: '18px' }}>
          These core rules are always on for your agent and can&apos;t be turned off. They prevent
          liability and ensure your agent never acts outside its defined role.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {GUARDRAIL_OPTIONS.map((guardrail) => {
          const isEscalateHighValue = guardrail.id === ESCALATE_HIGH_VALUE_ORDERS_GUARDRAIL_ID;
          const isEscalationExpanded = isEscalateHighValue;

          return (
            <Box
              key={guardrail.id}
              data-testid={`ella-onboarding-guardrail-${guardrail.id}`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                px: 2,
                py: 1.75,
                border: '1px solid',
                borderColor: 'border.main',
                borderRadius: '4px',
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="small" fontWeight="bold" sx={{ lineHeight: '20px' }}>
                      {guardrail.title}
                    </Typography>
                    {guardrail.locked ? (
                      <Chip
                        label="Always on"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: 'secondary.light',
                          color: 'text.secondary',
                          borderRadius: '4px',
                        }}
                      />
                    ) : null}
                  </Box>
                  <Typography variant="small" color="text.secondary" sx={{ lineHeight: '18px' }}>
                    {guardrail.description}
                  </Typography>
                </Box>

                {guardrail.locked ? (
                  <Box
                    data-testid={`ella-onboarding-guardrail-locked-${guardrail.id}`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flexShrink: 0,
                      mt: 0.25,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: 'border.main',
                      bgcolor: 'secondary.light',
                      color: 'text.secondary',
                    }}
                  >
                    <LockOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
                    <Typography variant="small" fontWeight="semibold" component="span">
                      Locked
                    </Typography>
                  </Box>
                ) : null}
              </Box>

              {isEscalationExpanded ? (
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'border.main',
                  }}
                >
                  <OnboardingEscalationFields embedded />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </>
  );
};
